/**
 * Quote Types — Sub-types for contracts, pricing, and verification.
 * The Quote type itself is now a backward-compat alias for Opportunity.
 */

import type { ProjectConfiguration } from "./types";

/* ═══════ BACKWARD COMPAT ═══════ */

export type {
  OpportunityStage as QuoteStatus,
  Opportunity as Quote,
} from "./opportunityTypes";

export {
  isWonStage as isWonStatus,
  isClosedStage as isClosedStatus,
  isActiveStage as isActiveStatus,
} from "./opportunityTypes";

/* ═══════ PRICING STATUS ═══════ */

export type PricingStatus = "Within Target" | "Below Target" | "Below Floor" | "Premium" | "Not Set";

export interface PricingGuardrails {
  targetPrice: number;
  floorPrice: number;
  premiumThreshold: number;
}

export interface PricingOverride {
  originalCalculatedPrice: number;
  adjustedPrice: number;
  variance: number;
  adjustmentAmount: number;
  reason: string;
  notes: string;
  appliedBy: string;
  appliedAt: string;
  requiresReview: boolean;
}

export type ScenarioMode = "derived" | "independent";

export interface QuoteScenario {
  id: string;
  name: string;
  mode: ScenarioMode;
  config: ProjectConfiguration;
  calculatedTotal: number;
  isSelected: boolean;
  isArchived: boolean;
  createdAt: string;
}

export interface DepositInfo {
  amount: number;
  method: string;
  date: string;
  notes?: string;
}

export type ContractStatus = "Draft" | "Pending" | "Signed" | "Voided" | "Cancelled";

export type CancelReason = "no_longer_doing_project" | "buyers_remorse" | "needs_spouse" | "price" | "other";

export const CANCEL_REASON_LABELS: Record<CancelReason, string> = {
  no_longer_doing_project: "No longer doing the project",
  buyers_remorse: "Buyer's remorse",
  needs_spouse: "Needs to speak with spouse",
  price: "Price",
  other: "Other",
};

export interface ContractCancellation {
  reason: CancelReason;
  customReason?: string;
  cancelledAt: string;
  cancelledBy: string;
}

export interface ContractDocument {
  id: string;
  quoteId: string;
  customerName: string;
  customerEmail: string;
  customerAddress: string;
  contractTotal: number;
  homeownerSignature?: string;
  homeownerSignedAt?: string;
  repSignature?: string;
  repSignedAt?: string;
  termsAccepted: boolean;
  rescissionAcknowledged: boolean;
  status: ContractStatus;
  createdAt: string;
  approvedAt?: string;
  sentToCustomerAt?: string;
  dispositionNotes?: string;
  ownershipType?: "Homeowner" | "POA";
  poaDocumentUploaded?: boolean;
  hoaInvolved?: boolean;
  cancellation?: ContractCancellation;
  selectedOptionsApproved?: boolean;
  configSnapshotHash?: string;
  configSnapshotAt?: string;
  paymentMethod?: string;
  depositAmount?: number;
  /** Optional private storage path for imported/uploaded contract PDFs. */
  filePath?: string;
  fileName?: string;
  mimeType?: string;
  fileSize?: number;
  uploadedAt?: string;
  attachmentId?: string;
}

export type PropertyType = "Single Family Home" | "Condo / Apartment" | "Townhome" | "Mobile Home" | "Other";
export type AccessDifficulty = "Low" | "Medium" | "High";
export type ParkingSituation = "Easy" | "Limited" | "Difficult";

export interface CondoDetails {
  floorNumber: string;
  elevatorAvailable: boolean;
  serviceElevator: boolean;
  hoaApprovalRequired: boolean;
}

export interface MobileHomeDetails {
  plumbingConstraintFlag: boolean;
  accessStructuralNotes: string;
}

export interface PropertyDetails {
  propertyType: PropertyType | "";
  accessDifficulty: AccessDifficulty | "";
  parkingSituation: ParkingSituation | "";
  condoDetails?: CondoDetails;
  mobileHomeDetails?: MobileHomeDetails;
}

export type OwnershipType = "Homeowner" | "POA" | "";

export interface CustomerVerification {
  primaryFirstName: string;
  primaryLastName: string;
  primaryPhone: string;
  primaryEmail: string;
  hasSecondaryContact: boolean;
  secondaryFirstName: string;
  secondaryLastName: string;
  secondaryPhone: string;
  secondaryEmail: string;
  addressVerified: boolean;
  propertyDetails: PropertyDetails;
  verificationComplete: boolean;
  ownershipType: OwnershipType;
  poaDocumentUploaded: boolean;
  hoaInvolved: boolean | null;
}

export const EMPTY_VERIFICATION: CustomerVerification = {
  primaryFirstName: "",
  primaryLastName: "",
  primaryPhone: "",
  primaryEmail: "",
  hasSecondaryContact: false,
  secondaryFirstName: "",
  secondaryLastName: "",
  secondaryPhone: "",
  secondaryEmail: "",
  addressVerified: false,
  propertyDetails: {
    propertyType: "",
    accessDifficulty: "",
    parkingSituation: "",
  },
  verificationComplete: false,
  ownershipType: "",
  poaDocumentUploaded: false,
  hoaInvolved: null,
};

export function isVerificationComplete(v: CustomerVerification, address?: string | null): boolean {
  if (!v.primaryFirstName?.trim() || !v.primaryLastName?.trim()) return false;
  if (!v.primaryPhone?.trim() || !v.primaryEmail?.trim()) return false;
  if (!address?.trim() || !v.addressVerified) return false;
  if (!v.propertyDetails?.propertyType) return false;
  if (!v.propertyDetails?.accessDifficulty) return false;
  if (!v.propertyDetails?.parkingSituation) return false;
  if (v.propertyDetails.propertyType === "Condo / Apartment") {
    const c = v.propertyDetails?.condoDetails;
    if (!c || !c.floorNumber?.trim()) return false;
  }
  if (!v.ownershipType) return false;
  if (v.ownershipType === "POA" && !v.poaDocumentUploaded) return false;
  if (v.hoaInvolved === null) return false;
  return true;
}

export interface VerificationErrors {
  primaryFirstName?: string;
  primaryLastName?: string;
  primaryPhone?: string;
  primaryEmail?: string;
  address?: string;
  addressVerified?: string;
  propertyType?: string;
  accessDifficulty?: string;
  parkingSituation?: string;
  ownershipType?: string;
  hoaInvolved?: string;
  floorNumber?: string;
  poaDocument?: string;
}

export function getVerificationErrors(v: CustomerVerification, address: string): VerificationErrors {
  const errors: VerificationErrors = {};
  const text = (value: unknown): string => (typeof value === "string" ? value.trim() : "");
  const propertyDetails = v?.propertyDetails ?? {};

  if (!text(v?.primaryFirstName)) {
    errors.primaryFirstName = "First name is required";
  }
  if (!text(v?.primaryLastName)) {
    errors.primaryLastName = "Last name is required";
  }
  if (!text(v?.primaryPhone)) {
    errors.primaryPhone = "Phone is required";
  }
  const email = text(v?.primaryEmail);
  if (!email) {
    errors.primaryEmail = "Email is required";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.primaryEmail = "Invalid email format";
  }
  if (!text(address)) {
    errors.address = "Address is required";
  } else if (!v?.addressVerified) {
    errors.addressVerified = "Address must be confirmed";
  }
  if (!propertyDetails.propertyType) {
    errors.propertyType = "Property type is required";
  }
  if (!propertyDetails.accessDifficulty) {
    errors.accessDifficulty = "Access difficulty is required";
  }
  if (!propertyDetails.parkingSituation) {
    errors.parkingSituation = "Parking situation is required";
  }
  if (!v?.ownershipType) {
    errors.ownershipType = "Ownership type is required";
  }
  if (v?.hoaInvolved === null || v?.hoaInvolved === undefined) {
    errors.hoaInvolved = "HOA involvement must be selected";
  }
  if (propertyDetails.propertyType === "Condo / Apartment" && propertyDetails.condoDetails) {
    if (!text(propertyDetails.condoDetails.floorNumber)) {
      errors.floorNumber = "Floor number is required";
    }
  }
  if (v?.ownershipType === "POA" && !v?.poaDocumentUploaded) {
    errors.poaDocument = "POA document is required";
  }

  return errors;
}

export function computePricingStatus(
  finalTotal: number,
  guardrails?: PricingGuardrails,
): PricingStatus {
  if (!guardrails || guardrails.targetPrice <= 0) return "Not Set";
  if (finalTotal < guardrails.floorPrice) return "Below Floor";
  if (finalTotal < guardrails.targetPrice) return "Below Target";
  if (guardrails.premiumThreshold > 0 && finalTotal >= guardrails.premiumThreshold) return "Premium";
  return "Within Target";
}

export const PRICING_STATUS_STYLES: Record<PricingStatus, { color: string; label: string }> = {
  "Within Target": { color: "bg-success/10 text-success border-success/30", label: "Within Target" },
  "Below Target": { color: "bg-warning/10 text-warning border-warning/30", label: "Below Target" },
  "Below Floor": { color: "bg-destructive/10 text-destructive border-destructive/30", label: "Below Floor — Review Required" },
  Premium: { color: "bg-primary/10 text-primary border-primary/30", label: "Premium" },
  "Not Set": { color: "bg-muted text-muted-foreground border-border", label: "Guardrails Not Set" },
};

/* ── Deposit/Financing helpers ── */
export const PAYMENT_METHODS = ["Financing", "Credit Card", "Check", "Cash", "Wire", "ACH"] as const;
export type PaymentMethod = typeof PAYMENT_METHODS[number];

export function getDepositDefaults(method: string): { amount: number; notes: string } {
  if (method === "Financing") {
    return { amount: 0, notes: "0 Down Financing" };
  }
  return { amount: 50, notes: "50% Down and 50% Upon Completion" };
}

export function isDepositValid(method: string, amount: number): boolean {
  if (method === "Financing") return true;
  return amount > 0;
}

export function formatQuoteReference(value?: string | null): string {
  const trimmed = value?.trim();
  if (!trimmed) return "QTE-000000";

  if (trimmed.startsWith("QTE-")) return trimmed;

  const oppMatch = trimmed.match(/^OPP-(\d+)$/i);
  if (oppMatch) {
    return `QTE-${oppMatch[1].padStart(6, "0")}`;
  }

  const qteMatch = trimmed.match(/^QTE?-(\d+)$/i);
  if (qteMatch) {
    return `QTE-${qteMatch[1].padStart(6, "0")}`;
  }

  return trimmed;
}

export function buildQuoteReference(sequence: number): string {
  return `QTE-${String(sequence).padStart(6, "0")}`;
}

/** @deprecated Legacy mapping. Use OpportunityStage directly. */
export function mapLegacyStatus(status: string): import("./opportunityTypes").OpportunityStage {
  switch (status) {
    case "Draft": case "In Progress": case "Presented": case "Open Opportunity": return "Appointment Completed";
    case "Won": case "Converted": case "Closed Won": return "Closed Won";
    case "Lost": case "Closed Lost": return "Closed Lost";
    case "Follow-Up Needed": return "Follow-Up Needed";
    default: return status as import("./opportunityTypes").OpportunityStage;
  }
}
